import { Component, OnInit, Input, OnDestroy } from "@angular/core";
import { EdpModalService } from "@edp-components/edp-modal";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getUrlToRedirect } from "@edp-store/selectors/contract.selectors";
import { ContractStatusEnum } from "@edp-enums/contract-status-enum";
import { EdpSelectList } from "@edp-models/edp-select-list";
import { Router } from "@angular/router";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { take, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { AppBannerService } from "@edp-services/app-banner.service";
import { DeepLinkService } from "@edp-services/deep-link.service";
import { getUser } from "@edp-store/selectors/user.selectors";
import { getContractEs, getContractsEs } from "@edp-store/selectors/ES/contract-es.selectors";
import { ContractEs } from "@edp-models/ES/es-contract.model";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { SelectContractEs } from "@edp-store/actions/ES/contract-es.actions";

const keyRouteRedirectLocalStorage = "redirectToRoute";

export interface ContractManagerArgs {
    account_id: string;
    user_id: string;
    nif: string;
    firstName: string;
    lastName: string;
}
@Component({
    selector: "edp-es-contract-list",
    templateUrl: "./edp-es-contract-list.component.html",
    styleUrls: ["./edp-es-contract-list.component.scss"],
})
export class EdpEsContractListComponent implements OnInit, OnDestroy {
    public contracts: ContractEs[];
    public tempContracts: ContractEs[];
    public ContractStatusEnum = ContractStatusEnum;
    public selectedContract: ContractEs;
    public hasManagedContracts: boolean;
    public hasPendingContractsWithToken: boolean;
    @Input() public data: IModalData;
    public isCollapsed = true;
    public contractsManagedByMe: EdpSelectList[] = [];
    public contractsManagedByOthers: EdpSelectList[] = [];
    public contractsPendingList: EdpSelectList[] = [];
    public multipleContractsName: string = null;
    public showMultipleContractsWarning: boolean;
    public activeTab: number;
    public disableRemoveButton = true;
    public selectListItemSelected: EdpSelectList;
    public isSearching = false;
    public isSearchable = false;
    public searchByText: string;
    public accessCode: string;
    public showAppBanner = false;
    public isLoading = false;
    public firebaseId: string;

    private unsubscribe = new Subject();

    public cameFromNewLocal = false;

    constructor(
        private store: Store<IAppState>,
        private storeEs: Store<IAppEsState>,
        private edpModalService: EdpModalService,
        private router: Router,
        private readonly appBannerService: AppBannerService,
        private deeplinkService: DeepLinkService
    ) {
        this.showAppBanner = this.appBannerService.show();
        this.storeEs.pipe(select(getContractsEs), takeUntil(this.unsubscribe)).subscribe(contracts => {
            if (contracts) {
                this.contracts = contracts;
                this.isSearchable = contracts.length > 1;
                this.tempContracts = this.contracts;
                if (this.isLoading) {
                    this.isLoading = false;
                    this.store.dispatch(new ShowLoader(false));
                }
            }
        });

        this.storeEs.pipe(select(getContractEs), takeUntil(this.unsubscribe)).subscribe(contract => {
            this.selectedContract = contract;
        });

        this.store.pipe(select(getUser)).subscribe(user => {
            if (user) {
                this.firebaseId = user.uid;
            }
        });
    }

    ngOnInit(): void {
        this.fillContractsSelectedLists();
        this.searchByText = "";

        if (!!this.data?.collection?.newLocal) {
            this.cameFromNewLocal = true;
        }
    }

    public dismiss(): void {
        this.edpModalService.close(false);
    }

    private navigateAndCheckQueryParams(url: string): void {
        this.router.navigate([url], { queryParamsHandling: "merge" });
    }

    public selectContract(contract: ContractEs): void {
        this.selectedContract = contract;
        this.store.dispatch(new SelectContractEs(contract));

        if (localStorage.getItem("local-deeplink")) {
            this.deeplinkService.setNewLocalDeeplink(localStorage.getItem("local-deeplink"), contract.id);
            localStorage.removeItem("local-deeplink");
            this.dismiss();
            return;
        }

        if (!!this.cameFromNewLocal && this.selectedContract.id !== contract.id) {
            this.router.navigate([`/locais-consumo/${this.selectedContract.id}/inicio`]);
            this.dismiss();
            return;
        }

        this.store.pipe(take(1), select(getUrlToRedirect)).subscribe(linkConfigured => {
            // TO DO - REDIRECT DEEP LINK
            // if (linkConfigured && this.authService.hasAccess(linkConfigured.ruleType, contract)) {
            //     if (linkConfigured.contractNeeded === "1") {
            //         this.store.dispatch(new HideContractSelection());
            //         this.navigateAndCheckQueryParams(`energia/${contract.id}/${linkConfigured.url}`);
            //     } else {
            //         this.navigateAndCheckQueryParams(linkConfigured.url);
            //     }
            // } else {
            !!this.hasRouteToRedirect() ? this.routeToRedirect() : this.navigateAndCheckQueryParams(`/energia/${contract.id}/inicio`);
            // }
        });

        this.dismiss();
    }

    private hasRouteToRedirect() {
        return !!localStorage.getItem(keyRouteRedirectLocalStorage);
    }

    private routeToRedirect() {
        const routeToRedirect = localStorage.getItem(keyRouteRedirectLocalStorage);
        localStorage.removeItem(keyRouteRedirectLocalStorage);
        this.router.navigate([routeToRedirect], { queryParamsHandling: "merge" });
    }

    public showSearchContract() {
        this.isSearching = !this.isSearching;
    }

    public onKeyPress(event: KeyboardEvent) {
        if (event.key === "Enter" || this.searchByText.length >= 0 || !this.searchByText.length) {
            this.tempContracts = this.contracts.filter(contract => {
                return contract.searchableFields().indexOf(this.searchByText.toLowerCase()) !== -1;
            });
        }
    }

    private fillContractsSelectedLists(): void {
        this.contractsManagedByMe = [];
        this.contractsManagedByOthers = [];
        this.showMultipleContractsWarning = false;
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    public openNewLocalModal() {
        this.router.navigate([`/locais-consumo/${this.selectedContract.id}/inicio`]);
        this.dismiss();
    }
}
